import type { PublicIntegrityAlert } from "@homesite/api-contract";
import { queryKeys } from "@homesite/queries";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/ApiClientContext";

/** Connection mode for the integrity alerts feed. */
export type IntegrityAlertsConnectionStatus = "live" | "polling" | "error";

export interface UsePublicIntegrityAlertsReturn {
	alerts: PublicIntegrityAlert[];
	/**
	 * "live" when a WebSocket connection is active (future Phase 5),
	 * "polling" for the current HTTP polling strategy,
	 * "error" when the last request failed.
	 */
	status: IntegrityAlertsConnectionStatus;
	isLoading: boolean;
	error: Error | null;
}

const POLL_INTERVAL_MS = 30_000;

/**
 * Fetches public integrity alerts, polling every 30 seconds.
 *
 * WebSocket swap-point: when `VITE_WS_URL` becomes a real endpoint (Phase 5),
 * replace the polling `useQuery` below with a `useEffect` that opens a
 * `socket.io-client` connection and calls `queryClient.setQueryData` on each
 * push event. The `status` return value should change to "live" while the
 * socket is open and fall back to "polling" on disconnect.
 */
export function usePublicIntegrityAlerts(): UsePublicIntegrityAlertsReturn {
	const client = useApiClient();

	const query = useQuery({
		queryKey: queryKeys.publicVerification.integrityAlerts.list(),
		queryFn: async () => {
			const result = await client.publicVerification.getIntegrityAlerts({
				query: { limit: 20, offset: 0 },
			});
			if (result.status === 200) {
				return result.body;
			}
			throw new Error("Failed to fetch integrity alerts");
		},
		refetchInterval: POLL_INTERVAL_MS,
	});

	const status: IntegrityAlertsConnectionStatus = query.isError
		? "error"
		: "polling";

	return {
		alerts: query.data?.items ?? [],
		status,
		isLoading: query.isLoading,
		error: query.error,
	};
}
